import React, {useContext, useEffect} from "react";
import {ModalDispatch} from "../context/ModalContext";
import {ConfigDispatch} from "../context/ConfigContext";
import {AuthContext} from "../context/LoginContext";
import WCLoginService from "../services/WCLoginService";
import PasswordChangeForm from "./PasswordChangeForm";
import LoginFormWrapper from "./LoginFormWrapper";
import {TextSectionsList} from "./TextSectionsList";
import SavedConfigurationList from "./SavedConfigurationList";
import {SavedConfigurationsDispatch} from "../context/SavedConfigurationsContext";
import ConfigurationsService from "../services/ConfigurationsService";
import {LoaderContext} from "../context/LoaderContext";
import logo from './../images/logo.png';
import logoEU from './../images/eu.png';
import InitConfig from "./InitConfig";

export const AppMainContainer = () => {
    //config
    const configDataDispatch = useContext(ConfigDispatch);
    //loader
    const loaderContext = useContext(LoaderContext);
    //get token and email from url
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const emailParam = urlParams.get('email');
    const [email, setEmail] = React.useState(emailParam);
    const [token, setToken] = React.useState(tokenParam);
    //modal
    const modalDispatch = useContext(ModalDispatch);
    //tabs
    const [loginTab, setLoginTab] = React.useState(false);
    const [configuratorTab, setConfiguratorTab] = React.useState(true);
    const [exportTab, setExportTab] = React.useState(false);
    //saved configurations
    const savedConfigurationsDispatch = useContext(SavedConfigurationsDispatch);
    //user data
    const user = useContext(AuthContext);
    // Rozmiar czcionki (domyślnie 62.5% na <html>)
    const [fontSize, setFontSize] = React.useState(62.5);
    // Stan dla wysokiego kontrastu
    const [highContrast, setHighContrast] = React.useState(false);

    // Zmiana rozmiaru czcionki na całej stronie (na <html>)
    useEffect(() => {
        document.documentElement.style.fontSize = `${fontSize}%`;
    }, [fontSize]);

    // Przełączanie kontrastu
    const toggleContrast = (e) => {
        if (e) e.preventDefault();
        setHighContrast(prev => !prev);
    };

    // Efekt dla zmiany kontrastu
    useEffect(() => {
        document.documentElement.classList.toggle('high-contrast', highContrast);
    }, [highContrast]);

    // Obsługa powiększania/pomniejszania tekstu
    const increaseFontSize = (e) => {
        if (e) e.preventDefault();
        setFontSize(prev => Math.min(prev + 10, 200)); // max 200%
    };
    const decreaseFontSize = (e) => {
        if (e) e.preventDefault();
        setFontSize(prev => Math.max(prev - 10, 40)); // min 40%
    };

    useEffect(() => {
        if (user.isLoggedIn) updateConfigurationList();
        if (email && token) checkCredentialsForResettingPassword(email, token);
    });

    //update saved configurations list
    const updateConfigurationList = async () => {
        const service = new ConfigurationsService();
        //get configurations list
        const saveConfigurationResponse = await service.getConfigurations();
        //console.log('saveConfigurationResponse', saveConfigurationResponse);
        //update configurations list
        savedConfigurationsDispatch({
            type: 'set',
            payload: saveConfigurationResponse
        });
    }
    //handle tabs switch
    const closeAllTabs = () => {
        setLoginTab(false);
        setConfiguratorTab(false);
        setExportTab(false);
    }
    const handleLoginTab = (e) => {
        e.preventDefault();
        closeAllTabs();
        setLoginTab(true);
    }
    const handleConfiguratorTab = (e) => {
        e.preventDefault();
        closeAllTabs();
        setConfiguratorTab(true);
    }

    async function checkCredentialsForResettingPassword(email, token) {
        const response = await WCLoginService.checkCredentialsForResettingPassword(email, token);
        if (response.status === 'success') {
            modalDispatch({
                type: 'showModal'
            });
            modalDispatch({
                type: 'setModalContent',
                payload: {
                    title: 'Potwierdź nowe hasło',
                    content:
                        <PasswordChangeForm
                            email={email}
                        />
                }
            });
        } else {
            modalDispatch({
                type: 'showModal'
            });
            modalDispatch({
                type: 'setModalContent',
                payload: {
                    title: 'Błąd',
                    content: '',
                }
            });
            modalDispatch({type: 'setErrorMsg', payload: response.message});
        }
        setEmail(null);
        setToken(null);
    }

    return (
        <div className={`App${highContrast ? ' App--high-contrast' : ''}`} role="main">
            <InitConfig/>
            <div className={`loader ${loaderContext.active ? 'active' : ''}`} aria-live="polite"
                 aria-busy={loaderContext.active}>
                <div className="loader__inner">
                    <div className="spinner-border" role="status" aria-label="Ładowanie">
                        <span className="sr-only"></span>
                    </div>
                </div>
            </div>
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <nav className="navigation__wrapper" aria-label="Główna nawigacja">
                        <ul className="navigation" role="menubar" aria-label="Menu główne">
                            <li className="navigation__item navigation__item--logo" role="none">
                                <a className={`navigation__link`} href="https://rodlew.dev.midero.com.pl" tabIndex="0"
                                   aria-label="Strona główna" role="menuitem">
                                    <img height={"50px"} src={logo} alt="Logo Rodlew Konf"/>
                                </a>
                            </li>
                            <li className="navigation__item" role="none">
                                <a
                                    id="conf-tab"
                                    className={`navigation__link ${configuratorTab ? 'disabled' : ''}`}
                                    onClick={handleConfiguratorTab}
                                    href={process.env.PUBLIC_URL + '/'}
                                    tabIndex="0"
                                    aria-current={configuratorTab ? "page" : undefined}
                                    aria-disabled={configuratorTab ? "true" : "false"}
                                    role="menuitem"
                                >
                                    Konfigurator
                                </a>
                            </li>
                            <li className="navigation__item" role="none">
                                <a
                                    className={`navigation__link ${loginTab ? 'disabled' : ''}`}
                                    onClick={handleLoginTab}
                                    href={process.env.PUBLIC_URL + '/'}
                                    tabIndex="0"
                                    aria-current={loginTab ? "page" : undefined}
                                    aria-disabled={loginTab ? "true" : "false"}
                                    role="menuitem"
                                >
                                    Moje konto
                                </a>
                            </li>
                            <li className="navigation__item" role="none">
                                <button
                                    className="navigation__link"
                                    onClick={increaseFontSize}
                                    aria-label="A+ Powiększ tekst"
                                    tabIndex="0"
                                    role="menuitem"
                                    style={{background: "none", border: "none", cursor: "pointer"}}
                                >
                                    A+
                                </button>
                            </li>
                            <li className="navigation__item" role="none">
                                <button
                                    className="navigation__link"
                                    onClick={decreaseFontSize}
                                    aria-label="A- Pomniejsz tekst"
                                    tabIndex="0"
                                    role="menuitem"
                                    style={{background: "none", border: "none", cursor: "pointer"}}
                                >
                                    A-
                                </button>
                            </li>
                            <li className="navigation__item" role="none">
                                <button
                                    className="navigation__link navigation__link--contrast"
                                    onClick={toggleContrast}
                                    aria-label={highContrast ? "Kontrast - Wyłącz wysoki kontrast" : "Kontrast + Włącz wysoki kontrast"}
                                    aria-checked={highContrast}
                                    tabIndex="0"
                                    role="menuitemcheckbox"
                                    style={{background: "none", border: "none", cursor: "pointer"}}
                                >
                                    {highContrast ? "Kontrast -" : "Kontrast +"}
                                </button>
                            </li>
                        </ul>
                        <div className="navigation__item navigation__item--eu-logo">
                            <img height={"50px"} src={logoEU} alt="Logo Unii Europejskiej"/>
                        </div>
                        </nav>
                    </div>
                    <div className="col-12">
                        {(loginTab && !user.isLoggedIn )&&
                            <div className="heading heading--center">
                                <div className="heading__line" aria-hidden="true"></div>
                                <h2 className="heading__title">Zaloguj się</h2>
                            </div>
                        }
                        {configuratorTab &&
                            <div className="heading heading--center">
                                <div className="heading__line" aria-hidden="true"></div>
                                <h1 className="heading__title" tabIndex="0">
                                    Konfigurator napisów
                                </h1>
                                <div className="heading__description">
                                    <p>
                                        Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy
                                        nibh
                                        euismod tincidunt ut laoreet dolore magna aliquam erat volutpat. Ut wisi
                                        enim ad
                                        minim veniam
                                    </p>
                                </div>
                            </div>
                        }
                    </div>
                    <div className="col-12">
                        {loginTab ? <LoginFormWrapper/> : null}
                        {configuratorTab ? <TextSectionsList/> : null}
                        {exportTab ? <SavedConfigurationList/> : null}
                    </div>
                </div>
            </div>
        </div>
    )
}
